import {BaseModel} from '@/models/base.model.js'
import {Channel} from '@/models/channel.model.js'
import {enrollmentStatuses} from '@/constants/enrollments.js'
import {addressTypes} from '@/constants/address-types.js'
import {contentTypes} from '@/constants/content.js'

/**
 * Enrollment model
 */
export class Enrollment extends BaseModel {

  //Sub models
  static subModels = {
    channel: Channel,
  }

  //Display name
  get displayName() {
    return (this.course?.name || this.name || '').replace(' [Course Generator]', '')
  }

  //Initials
  get initials() {
    return this.displayName
      .split(' ')
      .map(word => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  //Icon
  get icon() {
    return 'menu_book'
  }

  //Content type helper
  get contentType() {
    return this.course?.type ?? contentTypes.COURSE
  }
  get isCourse() {
    return this.contentType === contentTypes.COURSE
  }
  get isNudge() {
    return this.contentType === contentTypes.NUDGE
  }
  get isSurvey() {
    return this.contentType === contentTypes.SURVEY
  }

  //Content noun helpers
  get contentNoun() {
    if (this.contentType === contentTypes.NUDGE) {
      return 'Nudge'
    }
    if (this.contentType === contentTypes.SURVEY) {
      return 'Survey'
    }
    return 'Course'
  }
  get contentNounLowercase() {
    return this.contentNoun.toLowerCase()
  }

  //Status helper flags
  get isActive() {
    return this.status === enrollmentStatuses.ACTIVE
  }
  get isUpNext() {
    return this.status === enrollmentStatuses.UP_NEXT
  }
  get isScheduled() {
    return this.status === enrollmentStatuses.SCHEDULED
  }
  get isPaused() {
    return this.status === enrollmentStatuses.PAUSED
  }
  get isCanceled() {
    return this.status === enrollmentStatuses.CANCELED
  }
  get isOptedOut() {
    return this.status === enrollmentStatuses.OPTED_OUT
  }
  get isCompleted() {
    return this.status === enrollmentStatuses.COMPLETED
  }
  get isDisabled() {
    return this.status === enrollmentStatuses.DISABLED
  }
  get isNotEngaged() {
    return this.status === enrollmentStatuses.NOT_ENGAGED
  }
  get isWhatsappWindowViolated() {
    return this.status === enrollmentStatuses.WHATSAPP_WINDOW_VIOLATED
  }
  get isError() {
    return this.status === enrollmentStatuses.ERROR
  }

  //Setters for flags to avoid backend data from causing errors
  set isActive(value) {}

  //Check whether progress should be shown
  get shouldShowProgress() {
    return this.numSteps > 0 && (this.isActive || this.isPaused || this.isNotEngaged)
  }

  //Enrollment hasn't started yet
  get hasNotStartedYet() {
    return this.isUpNext || this.isScheduled
  }

  //Enrollment has ended, one way or another
  get hasEnded() {
    return (
      this.isCompleted ||
      this.isCanceled ||
      this.isOptedOut ||
      this.isDisabled ||
      this.isError ||
      this.isWhatsappWindowViolated
    )
  }

  //Can pause or resume
  get canPauseResume() {
    return this.isActive || this.isPaused || this.isNotEngaged
  }

  //Check if user can respond to the enrollment
  get canRespond() {
    return this.isActive || this.isPaused || this.isNotEngaged
  }

  //Check if this is a Teams enrollment
  get isTeams() {
    return (this.address?.addressType === addressTypes.TEAMS)
  }

  //Check if enrollment has a channel
  get hasChannel() {
    return !!this.channel
  }

  //A dynamic human readable status based on content type and enrollment status
  get statusText() {

    //Nudges
    if (this.contentType === contentTypes.NUDGE) {
      return 'You have a new message'
    }

    //Get content noun
    const {contentNoun, contentNounLowercase} = this

    //Surveys/courses
    switch (this.status) {
      case enrollmentStatuses.UP_NEXT:
      case enrollmentStatuses.SCHEDULED:
        return `This ${contentNounLowercase} will start shortly`
      case enrollmentStatuses.PAUSED:
        return `${contentNoun} has been paused`
      case enrollmentStatuses.CANCELED:
        return `${contentNoun} has been canceled`
      case enrollmentStatuses.OPTED_OUT:
        return `You opted out of this ${contentNounLowercase}`
      case enrollmentStatuses.COMPLETED:
        return `You finished this ${contentNounLowercase}`
      case enrollmentStatuses.DISABLED:
        return `${contentNoun} has been disabled`
      case enrollmentStatuses.WHATSAPP_WINDOW_VIOLATED:
      case enrollmentStatuses.ERROR:
        return `An error occurred during this ${contentNounLowercase}`
    }

    //Default for surveys
    if (this.contentType === contentTypes.SURVEY) {
      return `Please complete your ${contentNounLowercase}`
    }

    //Default for courses
    return `Continue your ${contentNounLowercase}`
  }
}
